import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Third Culture Leadership';
const SITE_URL = 'https://thirdcultureleadership.com';
const DEFAULT_IMAGE = 'https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/xa54zl60_A%20Third%20Culture%20is%20Possible%20.webp';
const DEFAULT_IMAGE_ALT = 'Third Culture Leadership — A Third Culture is Possible.';
const DEFAULT_DESCRIPTION = 'Third Culture Leadership equips leaders to navigate cultural complexity, communicate across difference, and build trusted relationships — without compromising identity, values, or integrity.';

/**
 * SEO Meta Component
 * 
 * Handles Open Graph and Twitter Card metadata for social sharing
 * 
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.image - OG image URL (absolute)
 * @param {string} props.url - Canonical URL path (e.g., '/about')
 * @param {string} props.type - OG type (default: 'website')
 * @param {string} props.imageWidth - OG image width
 * @param {string} props.imageHeight - OG image height
 * @param {string} props.imageAlt - OG image alt text
 * @param {string} props.twitterCard - Twitter card type (default: 'summary_large_image')
 */
const SEOMeta = ({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url = '',
  type = 'website',
  imageWidth = '1920',
  imageHeight = '900',
  imageAlt = DEFAULT_IMAGE_ALT,
  twitterCard = 'summary_large_image',
  noIndex = false,
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const canonicalUrl = `${SITE_URL}${url}`;
  const fullImageUrl = image.startsWith('http') ? image : `${SITE_URL}${image}`;
  const altText = imageAlt || title || DEFAULT_IMAGE_ALT;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:secure_url" content={fullImageUrl} />
      <meta property="og:image:width" content={imageWidth} />
      <meta property="og:image:height" content={imageHeight} />
      <meta property="og:image:alt" content={altText} />
      <meta property="og:locale" content="en_GB" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:image:alt" content={altText} />
    </Helmet>
  );
};

// Pre-defined meta configurations for common pages
export const BOOK_META = {
  title: 'Cultural Fluency Without Compromise',
  description: 'Thriving Across Cultures with Presence and Integrity. By Dr Promise Monday. Read the eBook on web, iOS, and Android. Free preview available.',
  image: DEFAULT_IMAGE,
  imageAlt: 'Cultural Fluency Without Compromise by Dr Promise Monday - Book Cover',
};

export const PAGE_META = {
  home: {
    title: 'Where Cultural Fluency Becomes a Leadership Advantage',
    description: 'Third Culture Leadership equips leaders to navigate cultural complexity, communicate across difference, and build trusted relationships — without compromising identity, values, or integrity.',
    image: DEFAULT_IMAGE,
    imageAlt: 'Third Culture Leadership — A Third Culture is Possible.',
  },
  about: {
    title: 'About Dr Promise Monday',
    description: 'Paediatrician, Author, and founder of Third Culture Leadership. Helping organisations navigate multicultural environments with clarity, dignity, and integrity.',
    image: DEFAULT_IMAGE,
    imageAlt: 'About Dr Promise Monday — Third Culture Leadership',
  },
  articles: {
    title: 'Articles',
    description: 'Insights on cultural intelligence, inclusive leadership, and building bridges across diverse communities. By Third Culture Leadership.',
    image: DEFAULT_IMAGE,
    imageAlt: 'Third Culture Leadership Articles',
  },
  contact: {
    title: 'Contact',
    description: 'Get in touch with Third Culture Leadership. Book a speaking engagement, request a consultation, or send a message.',
    image: DEFAULT_IMAGE,
    imageAlt: 'Contact Third Culture Leadership',
  },
  speaking: {
    title: 'Speaking',
    description: 'Book Dr Promise Monday for your next event. Keynotes and workshops on cultural fluency, inclusive leadership, and thriving across cultures.',
    image: DEFAULT_IMAGE,
    imageAlt: 'Speaking Engagements — Third Culture Leadership',
  },
  courses: {
    title: 'Self-Paced Course',
    description: 'Master cultural fluency with our comprehensive self-paced course. Learn to lead across cultures with confidence and integrity.',
    image: DEFAULT_IMAGE,
    imageAlt: 'Cultural Fluency Course — Third Culture Leadership',
  },
  cfqAssessment: {
    title: 'CFQ™ Assessment',
    description: 'Measure your Cultural Fluency Quotient with the CFQ™ Assessment. Discover your strengths and growth areas in cross-cultural leadership.',
    image: DEFAULT_IMAGE,
    imageAlt: 'CFQ Assessment — Third Culture Leadership',
  },
  bioLinks: {
    title: 'Links',
    description: 'Quick links to all Third Culture Leadership resources, social media, and contact information.',
    image: DEFAULT_IMAGE,
    imageAlt: 'Third Culture Leadership Links',
  },
  books: {
    title: 'Books',
    description: 'Explore transformative insights on cultural leadership and building bridges across diverse communities. By Dr Promise Monday.',
    image: 'https://customer-assets.emergentagent.com/job_d368e4dd-685c-4244-ae95-a5da53cd41bc/artifacts/op7w644a_font%20cover%20jpg.jpg',
    imageAlt: 'Books by Dr Promise Monday — Third Culture Leadership',
  },
};

export default SEOMeta;
